const React = require('react');
const T = require('prop-types');
const Phone = require('utils/phone');
const { default: Typography } = require('@mui/material/Typography');
const { default: IntlTelInput } = require('react-phone-input-2');
const { default: Styled } = require('styled-components');
const { normalizeSmsPreference } = require('utils/smsPreference');
const Styles = require('./styles.scss');

const Classes = Styles.default || Styles;

require('react-phone-input-2/lib/material.css');


const {
    useCallback,
    useImperativeHandle,
    useRef,
    useState,
    useEffect
} = React;

const internals = {};

internals.PHONE_ERROR_MESSAGE = 'Invalid phone number. Must be 10-15 digits long.';

internals.getDefaults = (preferences = {}) => {

    const {
        phone,
        phoneCountry
    } = preferences || {};

    const normalizedPhone = phone ? Phone.normalize(phone, phoneCountry || null) : null;

    return {
        phone: normalizedPhone?.phone || phone || '',
        phoneCountry: normalizedPhone?.phoneCountry || phoneCountry || 'us',
        dialCode: ''
    };
};

internals.isValidPhoneNumber = ({
    phone,
    phoneCountry
}) => Phone.isValid(phone, phoneCountry);

internals.TextAlertsPreference = function TextAlertsPreference(props, ref) {

    const {
        isEditPage,
        isSignupPage,
        onChange,
        phoneRequired,
        preferences
    } = props;

    const { PhoneContainer } = internals;

    const initialValues = internals.getDefaults(preferences);

    // The initialization code for IntlTelInput isn't great, so we update in useEffect.
    const [values, setValues] = useState(() => ({
        ...initialValues,
        phone: ''
    }));

    const initializedFromPreferencesRef = useRef(false);
    const [phoneTouched, setPhoneTouched] = useState(false);

    useEffect(() => {

        if (initializedFromPreferencesRef.current || !preferences) {
            return;
        }

        initializedFromPreferencesRef.current = true;

        setValues(
            internals.getDefaults(preferences)
        );
    }, [preferences]);

    const notifyChange = useCallback((field, value) => {

        if (typeof onChange === 'function') {
            onChange(field, value);
        }
    }, [onChange]);

    const validate = useCallback((field, nextValues = values) => {

        if (field !== 'phone') {
            return null;
        }

        if (phoneRequired && !nextValues.phone) {
            return 'Phone number is required for verification.';
        }

        if (nextValues.phone && (isEditPage || isSignupPage)) {
            const phoneCountry = nextValues.phoneCountry || 'us';

            if (!internals.isValidPhoneNumber({
                phone: nextValues.phone,
                phoneCountry
            })) {
                return internals.PHONE_ERROR_MESSAGE;
            }
        }

        return null;
    }, [isEditPage, isSignupPage, phoneRequired, values]);

    const hasErrors = useCallback(() => !!validate('phone'), [validate]);

    const getUserPreferences = useCallback((nextValues = values) => {

        const normalizedPhone = nextValues.phone
            ? Phone.normalize(nextValues.phone, nextValues.phoneCountry || null)
            : null;
        const phone = normalizedPhone?.phone || nextValues.phone || '';
        const phoneCountry = normalizedPhone?.phoneCountry || nextValues.phoneCountry || 'us';
        const sms = normalizeSmsPreference(
            !!phone && internals.isValidPhoneNumber({
                phone,
                phoneCountry
            })
        );

        return {
            phone,
            phoneCountry,
            sms
        };
    }, [values]);

    const setPhoneValue = useCallback((value, {
        countryCode,
        dialCode
    } = {}) => {

        setValues((currentValues) => {

            const nextValues = {
                ...currentValues,
                phone: value ? value : '',
                phoneCountry: countryCode || currentValues.phoneCountry || 'us',
                dialCode
            };
            const smsEnabled = !!nextValues.phone && internals.isValidPhoneNumber({
                phone: nextValues.phone,
                phoneCountry: nextValues.phoneCountry
            });

            notifyChange('phone', nextValues.phone);
            notifyChange('phoneCountry', nextValues.phoneCountry);
            notifyChange('sms', normalizeSmsPreference(smsEnabled));

            return nextValues;
        });
    }, [notifyChange]);

    const submit = useCallback(() => {

        setPhoneTouched(true);
        return !hasErrors();
    }, [hasErrors]);

    useImperativeHandle(ref, () => ({
        fields: ['phone'],
        validate,
        submit,
        getUserPreferences,
        hasErrors
    }), [
        getUserPreferences,
        hasErrors,
        submit,
        validate
    ]);

    const phoneError = phoneTouched ? validate('phone') : null;
    const phoneInputClassName = `nearpeer-text-alert-phone${phoneError ? ' nearpeer-text-alert-phone--error' : ''}`;

    return <div className={Classes.wrapper}>
        <PhoneContainer>
            <IntlTelInput
                value={values.phone}
                country={values.phoneCountry || 'us'}
                containerClass={phoneInputClassName}
                inputProps={{
                    'aria-label': phoneRequired
                        ? 'Cell Number required for verification input'
                        : 'Cell Number for text alerts input',
                    'aria-describedby': phoneError
                        ? 'text-alert-phone-error-message'
                        : undefined,
                    'aria-invalid': !!phoneError || undefined,
                    'aria-required': phoneRequired || undefined,
                    required: phoneRequired || undefined
                }}
                onChange={setPhoneValue}
                onBlur={() => setPhoneTouched(true)}
                preferredCountries={['us']}
                placeholder={'(402) 123-4567'}
                specialLabel={
                    phoneRequired
                        ? 'Cell Number (required for verification)'
                        : 'Cell Number (for text alerts)'
                }
                isValid={(inputNumber, country) => {

                    if (inputNumber) {
                        return internals.isValidPhoneNumber({
                            phone: inputNumber,
                            phoneCountry: country.iso2
                        });
                    }

                    return true;
                }}
                disableCountryCode
                disableCountryGuess
            />
            {phoneError ? <Typography
                color={'error'}
                id='text-alert-phone-error-message'
                role='alert'
                aria-live='assertive'
                className={Classes.errorText}
            >
                {phoneError}
            </Typography> : null}
        </PhoneContainer>
    </div>;
};

const TextAlertsPreference = React.forwardRef(internals.TextAlertsPreference);

TextAlertsPreference.propTypes = {
    isEditPage: T.bool,
    isSignupPage: T.bool,
    onChange: T.func,
    phoneRequired: T.bool,
    preferences: T.object
};

internals.PhoneContainer = Styled.div`
    margin: 16px 0;
`;

module.exports = TextAlertsPreference;
module.exports.internals = internals;
